import { Grid, TextField } from "@mui/material";
import { useState } from "react";
import { Form, Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Notification from "../popup/Notification";
import ConfirmDialog from "../dialog/ConfirmDialog";
import Stack from "@mui/joy/Stack";
import Button from "@mui/joy/Button";
import useAuth from "../hooks/useAuth";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { LightTooltip } from "../tooltip/ToolTip";
import Autocomplete from "@mui/material/Autocomplete";
import StringValidation from "../reusables/yupvalidation/StringValidation";
import NumberValidation from "../reusables/yupvalidation/NumberValidation";

const AddExpense = (props) => {
  const { closeDialog, editExpense, selectedExpense } = props;
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();

  var expenseInfo = null;
  var expenseId = "";
  var amount = 0;
  var description = "";
  var type = 0;

  var defaultExpenseType = null;

  if (editExpense) {
    expenseInfo = JSON.parse(selectedExpense);
    expenseId = expenseInfo.id;
    amount = expenseInfo.amount;
    description = expenseInfo.description;
    type = expenseInfo.type;
    defaultExpenseType = expenseTypeOptions[type - 1];
  }

  const resetForm = (props) => {
    // clear input fields
    var now = new Date();
    props.resetForm();
    setExpenseType("");
    setSelectedExpenseTypeKey(now);
  };

  const closeForm = (message) => {
    closeDialog(message);
  };

  // Expense type
  const [expenseType, setExpenseType] = useState(editExpense ? type : 0);
  const [selectedExpenseTypeKey, setSelectedExpenseTypeKey] =
    useState("typeKey");
  const [expenseTypeOptionsEmpty, setExpenseTypeOptionEmpty] = useState(false);
  const handleExpenseTypeOptionChange = (event, value) => {
    setExpenseType(value.id);
    setExpenseTypeOptionEmpty(false);
  };

  // Validate form
  const validationSchema = Yup.object().shape({
    amount: NumberValidation(1, 1000000),
    description: StringValidation(2, 50),
  });

  // Common
  const initialValues = editExpense
    ? {
        amount: amount,
        description: description,
      }
    : {
        amount: "",
        description: "",
      };

  function onSubmit(values, props) {
    // Expense type
    if (editExpense && expenseTypeOptionsEmpty) {
      // Please select expense type
      setExpenseTypeOptionEmpty(true);
      props.setSubmitting(false);
      return;
    }

    props.setSubmitting(true);
    createExpense(values, props);
  }

  const EXPENSE_URL = "/api/v1/expenses/";
  const createExpense = async (values, props) => {
    // Common
    const data = {
      amount: values.amount,
      description: values.description,
      type: expenseType,
      tenantId: auth.tenantId,
    };

    try {
      const response = editExpense
        ? await axiosPrivate.put(EXPENSE_URL + expenseId, data, {
            headers: {
              "Content-Type": "application/json",
            },
          })
        : await axiosPrivate.post(EXPENSE_URL + "save", data, {
            headers: {
              "Content-Type": "application/json",
            },
          });

      //console.log(response.data.message);
      props.setSubmitting(false);
      resetForm(props);
      closeForm(response.data.message);
    } catch (error) {
      props.setSubmitting(false);
      var errorMessage = "";
      if (error.response) {
        // The client was given an error response (5xx, 4xx)
        const errorData = error.response.data;
        errorMessage = errorData.message;
      } else if (error.request) {
        // The client never received a response, and the request was never left
        errorMessage = "No server response";
      } else {
        // Anything else
        errorMessage = "Unexpected error occurred";
      }
      const errorTitle = editExpense ? "Update" : "Create";
      setConfirmDialog({
        isOpen: true,
        title: `Failed To ${errorTitle} Expense`,
        subTitle: errorMessage,
        action: "Retry",
        onConfirm: { onSubmit },
      });
    }
  };

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });

  return (
    <Grid>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {(props) => (
          <Form>
            <Stack spacing={2} mt={3} direction="column">
              <Field
                as={TextField}
                label="Amount"
                name="amount"
                placeholder="Enter amount"
                fullWidth
                required
                helperText={
                  <ErrorMessage name="amount">
                    {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                  </ErrorMessage>
                }
              />

              <Field
                as={TextField}
                label="Ente description"
                name="description"
                placeholder="Enter description"
                fullWidth
                required
                helperText={
                  <ErrorMessage name="description">
                    {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                  </ErrorMessage>
                }
              />

              <Autocomplete
                id="expenseType"
                defaultValue={defaultExpenseType}
                onChange={handleExpenseTypeOptionChange}
                key={selectedExpenseTypeKey}
                isOptionEqualToValue={(expenseType, value) =>
                  expenseType.name === value.name
                }
                getOptionLabel={(expenseType) =>
                  expenseType.name ? expenseType.name : ""
                }
                fullWidth
                options={expenseTypeOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Expense type"
                    placeholder="Expense type"
                    error={expenseTypeOptionsEmpty}
                    helperText={
                      expenseTypeOptionsEmpty ? "Expense type required" : ""
                    }
                  />
                )}
              />
            </Stack>

            <Stack spacing={2} mt={3} direction="row" justifyContent="flex-end">
              <LightTooltip title="Clear form">
                <Button
                  variant="contained"
                  sx={{
                    borderRadius: 8,
                    width: 120,
                    backgroundColor: "#808080",
                    padding: "7px 14px",
                    fontSize: "14px",
                    color: "white",
                    ":hover": {
                      bgcolor: "#696969",
                      color: "white",
                    },
                  }}
                  onClick={() => {
                    resetForm(props);
                  }}
                >
                  Reset
                </Button>
              </LightTooltip>

              <LightTooltip title="Submit details">
                <Button
                  loading={props.isSubmitting}
                  loadingPosition="start"
                  disabled={props.isSubmitting}
                  type="submit"
                  sx={{
                    width: 120,
                  }}
                  variant="solid"
                >
                  {props.isSubmitting ? "Submitting" : "Submit"}
                </Button>
              </LightTooltip>
            </Stack>
          </Form>
        )}
      </Formik>

      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
    </Grid>
  );
};

export default AddExpense;

const expenseTypeOptions = [
  { id: 1, name: "Daily" },
  { id: 2, name: "Weekly" },
  { id: 3, name: "Monthly" },
  { id: 4, name: "Yearly" },
];
